import type { JSX } from 'react';
import { LuEye, LuListTodo } from 'react-icons/lu';

import { TaskStatus } from '@ndvr/customer-api';

import { Card, CardContent } from '@/components/ui/card';
import { formatDate, titleCase } from 'utils';
import { TaskStatusBadge } from 'views/tasks/widgets/TaskBadge';

import { EventTaskView, getTaskPreview } from './event-details-shared';

interface EventDetailsTasksSectionProps {
    onTaskClick: (taskGuid: string) => void;
    tasks: EventTaskView[];
}

interface EventTaskCardProps {
    onClick: (taskGuid: string) => void;
    task: EventTaskView;
}

export function EventDetailsTasksSection({ onTaskClick, tasks }: EventDetailsTasksSectionProps): JSX.Element | null {
    if (!tasks.length) {
        return null;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <LuListTodo className="text-muted-foreground h-4 w-4" />
                <h4 className="text-sm font-medium">Related Tasks</h4>
            </div>
            <div className="space-y-2">
                {tasks.map((task) => (
                    <EventTaskCard key={task.taskGUID} task={task} onClick={onTaskClick} />
                ))}
            </div>
        </div>
    );
}

function EventTaskCard({ onClick, task }: EventTaskCardProps): JSX.Element {
    return (
        <Card className="hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => onClick(task.taskGUID)}>
            <CardContent className="p-3">
                <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                        <h5 className="mb-1 truncate text-sm font-medium">{task.title}</h5>
                        <div className="my-1">
                            <TaskStatusBadge status={task.status} />
                        </div>
                        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                            {getTaskPreview(task.description)}
                        </p>
                        <div className="mt-2 flex items-center gap-3">
                            {task.overdue && task.status !== TaskStatus.COMPLETED ? (
                                <span className="text-xs font-medium text-red-600">
                                    Due: {formatDate(task.dueDate)} (Overdue)
                                </span>
                            ) : (
                                <span className="text-xs">Due: {formatDate(task.dueDate)}</span>
                            )}
                            <span className="text-muted-foreground text-xs">{titleCase(task.type)}</span>
                        </div>
                    </div>
                    <LuEye className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                </div>
            </CardContent>
        </Card>
    );
}
